import { useState, useCallback } from 'react';
import {
  InvoicingDocument,
  InvoicingDocumentResponse,
  SearchInvoicingDocumentsRequest,
  ACLAuthorizationRequest,
  SearchInvoicingDocumentsPostContentTypeEnum,
  CreateInvoicingDocumentContentTypeEnum,
} from '@/api/bol';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { getInvoicingDocumentsApi } from '@/services/bolApiClient';
import { extractErrorMessage, formatOperationError } from '@/utils/errorUtils';
import { getLogisticsObjectDetailPath } from '@/types/logisticsObject';
import { getEffectiveLogisticsObjectSearchQuery } from '@/utils/searchDefaults';

export interface UseInvoicingDocumentsReturn {
  invoicingDocuments: InvoicingDocumentResponse[];
  loading: boolean;
  searchError: string | null;
  selectedInvoicingDocument: InvoicingDocumentResponse | null;
  setSelectedInvoicingDocument: (invoicingDocument: InvoicingDocumentResponse | null) => void;
  fetchInvoicingDocuments: (page?: number, pageSize?: number, searchBody?: SearchInvoicingDocumentsRequest) => Promise<boolean>;
  createInvoicingDocument: (invoicingDocumentData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => Promise<void>;
  deleteInvoicingDocument: (invoicingDocumentId: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const useInvoicingDocuments = (): UseInvoicingDocumentsReturn => {
  const [items, setItems] = useState<InvoicingDocumentResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedInvoicingDocument, setSelectedInvoicingDocument] = useState<InvoicingDocumentResponse | null>(null);
  const [total, setTotal] = useState(0);

  const { showSuccess, showError } = useSnackbarNotification();

  const fetchInvoicingDocuments = useCallback(async (page: number = 0, pageSize: number = 10, searchBody?: SearchInvoicingDocumentsRequest): Promise<boolean> => {
    const normalizedSearchBody = getEffectiveLogisticsObjectSearchQuery(searchBody);
    setLoading(true);
    setSearchError(null);

    try {
      const api = getInvoicingDocumentsApi();
      const response = await api.searchInvoicingDocumentsPost({
        contentType: SearchInvoicingDocumentsPostContentTypeEnum.ApplicationJson,
        searchInvoicingDocumentsRequest: normalizedSearchBody,
        limit: pageSize,
        offset: page * pageSize,
      });

      setItems(response.results || []);
      setTotal(response.pagination?.total || 0);
      return true;
    } catch (err: unknown) {
      const errorMessage = await extractErrorMessage(err, 'Failed to fetch invoicing-documents');
      setSearchError(errorMessage);
      showError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const createInvoicingDocument = useCallback(async (invoicingDocumentData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => {
    try {
      const api = getInvoicingDocumentsApi();

      const createdInvoicingDocument = await api.createInvoicingDocument({
        contentType: CreateInvoicingDocumentContentTypeEnum.ApplicationJson,
        xBOLMemberIdentifier: memberIdentifier,
        invoicingDocument: invoicingDocumentData as InvoicingDocument,
        source: true,
        xBOLACLAuthorizations: aclAuthorizations ? JSON.stringify(aclAuthorizations) : undefined,
      });

      const createdInvoicingDocumentId = createdInvoicingDocument?.id;

      showSuccess(
        'InvoicingDocument created successfully',
        createdInvoicingDocumentId
          ? {
              link: {
                label: 'View details',
                href: getLogisticsObjectDetailPath('invoicing-document', createdInvoicingDocumentId),
              },
            }
          : undefined,
      );
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Create invoicing-document', 'Failed to create invoicing-document');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  const deleteInvoicingDocument = useCallback(async (invoicingDocumentId: string) => {
    try {
      const api = getInvoicingDocumentsApi();
      await api.deleteInvoicingDocumentById({ invoicingDocumentId });
      showSuccess('InvoicingDocument deleted successfully');
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Delete invoicing-document', 'Failed to delete invoicing-document');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  return {
    invoicingDocuments: items,
    loading,
    searchError,
    selectedInvoicingDocument,
    setSelectedInvoicingDocument,
    fetchInvoicingDocuments,
    createInvoicingDocument,
    deleteInvoicingDocument,
    setTotal,
    total,
  };
};
